import React, { useState } from 'react';
import { useForm, FormContext } from 'react-hook-form';
import { Enums } from '@letsdothis/tracking';

import { Textarea } from '@shared/ReactHooksForm';
import { ActivityType } from '@/types/user';

import useReply from '../../hooks/useReply';
import { Activity } from '../../types';
import Button from '../Button';
import Reply from '../Reply';
import HighFive, { HighFiveOptions } from '../HighFive';

import styles from './styles.module.css';

export type ReactionsProps = {
  activity: Activity;
  activityType: ActivityType;
  eventId: string;
  loginReturnUrl?: string;
  replyOptions: {
    contentCapturedContentType: Enums.ContentCapturedContentType;
    initiallyOpen?: boolean;
    onSubmit?: () => void;
    onFocus?: () => void;
  };
  highFiveOptions: HighFiveOptions;
};

const Reactions: React.FC<ReactionsProps> = (props: ReactionsProps) => {
  const {
    activity,
    activityType,
    eventId,
    highFiveOptions,
    replyOptions,
    loginReturnUrl,
  } = props;
  const {
    contentCapturedContentType,
    onSubmit = () => {},
    onFocus = () => {},
    initiallyOpen = false,
  } = replyOptions;
  const [open, setOpen] = useState(initiallyOpen);
  const [lastReply, setLastReply] = useState('');
  const form = useForm({
    mode: 'onBlur',
    validateCriteriaMode: 'all',
  });
  const activityId = activity?.id;
  const value = (form.watch(activityId) || '').trim();
  const { handleSubmit } = form;
  const [reply, { loading }] = useReply({
    activity,
    eventId,
    activityType,
    contentCapturedContentType,
  });

  const onFormSubmit = async (data) => {
    onSubmit();
    const text = (data[activityId] || '').trim();
    if (!text || loading) {
      return;
    }
    setOpen(false);
    setLastReply(text);
    await reply(text);
  };
  const forceExpand = initiallyOpen && !lastReply;

  return (
    <div className={styles.container}>
      <div className={styles.actions}>
        {!forceExpand && (
          <Reply
            disabled={loading}
            text={open ? 'Cancel' : 'Reply'}
            onClick={() => {
              setOpen(!open);
            }}
            title={open ? 'Cancel' : 'Reply'}
          />
        )}
        <HighFive
          {...{
            activity,
            activityType,
            eventId,
            loginReturnUrl,
            showHighFives: true,
          }}
          {...highFiveOptions}
        />
      </div>
      {open && (
        <FormContext {...form}>
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            className={styles.container}
          >
            <Textarea
              id={activityId}
              title="Message"
              hideLabel
              onFocus={onFocus}
              autoFocus={!forceExpand}
              expandHeightToFit
              rows={1}
            />
            <div className={styles.actions}>
              <Button
                type="submit"
                disabled={!value || loading}
                text="Post"
                title="Post"
                bold
                active={!!value}
              />
            </div>
          </form>
        </FormContext>
      )}
    </div>
  );
};

export default Reactions;
